import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Product, apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// 购物车项目类型
export interface CartItem {
  product: Product;
  quantity: number;
}

// 购物车状态类型
interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}

// 购物车操作类型
type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { productId: number } }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: number; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: { items: CartItem[] } };

// 购物车上下文类型
interface CartContextType {
  state: CartState;
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (productId: number) => number;
  isInCart: (productId: number) => boolean;
}

// 初始状态
const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
};

// 购物车Reducer
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity } = action.payload;
      const existingItem = state.items.find(item => item.product.id === product.id);
      
      let newItems: CartItem[];
      if (existingItem) {
        newItems = state.items.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newItems = [...state.items, { product, quantity }];
      }
      
      return calculateTotals(newItems);
    }
    
    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(item => item.product.id !== action.payload.productId);
      return calculateTotals(newItems);
    }
    
    case 'UPDATE_QUANTITY': {
      const { productId, quantity } = action.payload;
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_ITEM', payload: { productId } });
      }
      
      const newItems = state.items.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      );
      
      return calculateTotals(newItems);
    }
    
    case 'CLEAR_CART':
      return initialState;
    
    case 'LOAD_CART':
      return calculateTotals(action.payload.items);
    
    default:
      return state;
  }
}

// 计算总价和总数量
function calculateTotals(items: CartItem[]): CartState {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + ((item.product.price || 0) * item.quantity), 0);
  
  return {
    items,
    totalItems,
    totalPrice,
  };
}

// 创建上下文
const CartContext = createContext<CartContextType | undefined>(undefined);

// 购物车Provider组件
export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { isAuthenticated } = useAuth();

  // 从服务器加载购物车数据
  const loadCartFromServer = async () => {
    if (!isAuthenticated) {
      return;
    }
    
    try {
      const response = await apiService.getCart();
      if (response.success && response.cart) {
        // 将服务器返回的购物车数据转换为前端格式
        const cartItems: CartItem[] = response.cart.items.map((item: any) => {
          // 处理媒体图库数据，按sortOrder排序
          const sortedMediaList = (item.goods.mediaGalleryList || [])
            .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
            .map((media: any) => ({
              id: media.id,
              name: media.name,
              originalFilename: media.originalFilename,
              fileUrl: media.fileUrl,
              contentType: media.contentType,
              fileSize: media.fileSize,
              description: media.description,
              sortOrder: media.sortOrder,
              isPrimary: media.isPrimary
            }));
          
          // 使用第一张图片作为imageUrl
          const firstImageUrl = sortedMediaList.length > 0 ? sortedMediaList[0].fileUrl : '/img/placeholder.png';
          
          return {
            product: {
              id: item.goods.id,
              name: item.goods.name,
              description: item.goods.description,
              price: parseFloat(item.goods.price || 0),
              imageUrl: firstImageUrl, // 使用第一张图片
              stock: 999, // 默认库存，因为Goods实体没有stock字段
              category: 'default', // 默认分类，因为Goods实体没有category字段
              active: item.goods.active !== undefined ? item.goods.active : true,
              sku: `SKU-${item.goods.id}`,
              createdAt: item.createdAt || new Date().toISOString(),
              updatedAt: item.updatedAt || new Date().toISOString(),
              mediaGalleryList: sortedMediaList
            },
            quantity: item.quantity
          };
        });
        
        dispatch({ type: 'LOAD_CART', payload: { items: cartItems } });
      }
    } catch (error) {
      console.error('加载购物车失败:', error);
    }
  };

  // 当用户登录状态改变时，加载购物车
  useEffect(() => {
    if (isAuthenticated) {
      loadCartFromServer();
    } else {
      // 用户未登录时清空购物车
      dispatch({ type: 'CLEAR_CART' });
    }
  }, [isAuthenticated]);

  // 添加商品到购物车
  const addItem = async (product: Product, quantity: number = 1) => {
    // 确保使用第一张图片作为imageUrl
    const productWithFirstImage = {
      ...product,
      imageUrl: (product.mediaGalleryList && product.mediaGalleryList.length > 0) 
        ? product.mediaGalleryList[0].ossPath 
        : product.imageUrl || '/img/placeholder.png'
    };
    
    if (!isAuthenticated) {
      // 如果用户未登录，可以先添加到本地状态，登录后再同步到服务器
      dispatch({ type: 'ADD_ITEM', payload: { product: productWithFirstImage, quantity } });
      return;
    }
    
    try {
      const response = await apiService.addToCart(product.id, quantity);
      if (response.success) {
        // 从服务器重新加载购物车数据
        await loadCartFromServer();
      }
    } catch (error) {
      console.error('添加商品到购物车失败:', error);
      // 失败时回退到本地状态
      dispatch({ type: 'ADD_ITEM', payload: { product: productWithFirstImage, quantity } });
    }
  };

  // 从购物车移除商品
  const removeItem = async (productId: number) => {
    if (!isAuthenticated) {
      dispatch({ type: 'REMOVE_ITEM', payload: { productId } });
      return;
    }
    
    try {
      const response = await apiService.removeFromCart(productId);
      if (response.success) {
        await loadCartFromServer();
      }
    } catch (error) {
      console.error('移除商品失败:', error);
      dispatch({ type: 'REMOVE_ITEM', payload: { productId } });
    }
  };

  // 更新商品数量
  const updateQuantity = async (productId: number, quantity: number) => {
    if (!isAuthenticated) {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
      return;
    }
    
    try {
      const response = await apiService.updateCartItemQuantity(productId, quantity);
      if (response.success) {
        await loadCartFromServer();
      }
    } catch (error) {
      console.error('更新商品数量失败:', error);
      dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
    }
  };

  // 清空购物车
  const clearCart = async () => {
    if (!isAuthenticated) {
      dispatch({ type: 'CLEAR_CART' });
      return;
    }
    
    try {
      const response = await apiService.clearCart();
      if (response.success) {
        await loadCartFromServer();
      }
    } catch (error) {
      console.error('清空购物车失败:', error);
      dispatch({ type: 'CLEAR_CART' });
    }
  };

  // 获取商品在购物车中的数量
  const getItemQuantity = (productId: number): number => {
    const item = state.items.find(item => item.product.id === productId);
    return item ? item.quantity : 0;
  };

  // 检查商品是否在购物车中
  const isInCart = (productId: number): boolean => {
    return state.items.some(item => item.product.id === productId);
  };

  const value: CartContextType = {
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isInCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

// 使用购物车上下文的Hook
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
